/**
 * MinistrareIA - WhatsApp Service
 * Service for WhatsApp API communication
 * 
 * UPDATED: Added sendMessageWithMedia for text + multiple files
 */

import api from '../global/api';

const WHATSAPP_BASE = '/api/whatsapp';

/**
 * WhatsApp Service Class
 */
class WhatsAppService {
  // ============================================
  // CONNECTION
  // ============================================

  /**
   * Get connection status
   */
  async getConnectionStatus() {
    const response = await api.get(`${WHATSAPP_BASE}/status`);
    return response.data;
  }

  /**
   * Get QR code for connection
   */
  async getQRCode() {
    const response = await api.get(`${WHATSAPP_BASE}/qrcode`);
    return response.data;
  }

  /**
   * Disconnect WhatsApp
   */
  async disconnect() {
    const response = await api.post(`${WHATSAPP_BASE}/disconnect`);
    return response.data;
  }

  /**
   * Reconnect WhatsApp (restart + new QR)
   */
  async reconnect() {
    const response = await api.post(`${WHATSAPP_BASE}/reconnect`);
    return response.data;
  }

  /**
   * Recreate WhatsApp instance from scratch
   * 
   * Use when instance is in error state and cannot generate QR code.
   * This will delete the existing instance and create a new one.
   * 
   * @returns {Promise<Object>} Result with QR code data
   */
  async recreateInstance() {
    console.log('[WhatsApp] Recreating instance...');
    const response = await api.post(`${WHATSAPP_BASE}/recreate-instance`);
    return response.data;
  }

  // ============================================
  // CHATS
  // ============================================

  /**
   * List all chats
   */
  async getChats() {
    const response = await api.get(`${WHATSAPP_BASE}/chats`);
    return response.data;
  }

  /**
   * Get messages from a chat
   * @param {string} remoteJid - Chat JID
   * @param {number} limit - Max messages to return
   */
  async getChatMessages(remoteJid, limit = 50) {
    const encodedJid = encodeURIComponent(remoteJid);
    const response = await api.get(
      `${WHATSAPP_BASE}/chats/${encodedJid}/messages?limit=${limit}`
    );
    return response.data;
  }

  // ============================================
  // MESSAGES
  // ============================================

  /**
   * Send text message
   * @param {string} number - Phone number
   * @param {string} text - Message text
   */
  async sendMessage(number, text) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/send`, {
      number,
      text,
    });
    return response.data;
  }

  /**
   * Send media message (single file)
   * @param {string} number - Phone number
   * @param {string} mediaType - image, video, document, audio
   * @param {string} mediaUrl - URL of the media
   * @param {string} caption - Optional caption
   * @param {string} fileName - Optional filename for documents
   */
  async sendMedia(number, mediaType, mediaUrl, caption = null, fileName = null) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/send-media`, {
      number,
      media_type: mediaType,
      media_url: mediaUrl,
      caption,
      file_name: fileName,
    });
    return response.data;
  }

  /**
   * Send message with multiple file attachments
   * 
   * This is the primary method for sending messages from AI Workspace
   * that may include multiple files (PDFs, images, videos, etc).
   * 
   * @param {string} number - Phone number (with country code, no +)
   * @param {string} text - Optional text message
   * @param {Array} files - Array of file objects
   * @param {string} files[].url - File URL (Azure Blob Storage)
   * @param {string} files[].type - File type: image, video, audio, document, pdf
   * @param {string} files[].filename - Original filename
   * @param {string} files[].mimeType - Optional MIME type
   * 
   * @returns {Object} Result with success status, message IDs, file results
   * 
   * @example
   * // Send text only
   * await sendMessageWithMedia('5511999999999', 'Hello!', []);
   * 
   * @example
   * // Send files only
   * await sendMessageWithMedia('5511999999999', '', [
   *   { url: 'https://...', type: 'pdf', filename: 'doc.pdf' }
   * ]);
   * 
   * @example
   * // Send text + files
   * await sendMessageWithMedia('5511999999999', 'Here are the docs:', [
   *   { url: 'https://...', type: 'pdf', filename: 'proposal.pdf' },
   *   { url: 'https://...', type: 'image', filename: 'chart.png' }
   * ]);
   */
  async sendMessageWithMedia(number, text, files = []) {
    // Validate: must have text or files
    const hasText = text && text.trim();
    const hasFiles = files && files.length > 0;
    
    if (!hasText && !hasFiles) {
      throw new Error('Must provide text or files (or both)');
    }

    // Prepare files payload
    const filesPayload = hasFiles ? files.map(f => ({
      url: f.url,
      type: f.type || 'document',  // Default to document
      filename: f.filename || this._extractFilename(f.url),
      mimeType: f.mimeType || null,
    })) : [];

    console.log('[WhatsAppService] sendMessageWithMedia:', {
      number,
      textLength: text?.length || 0,
      filesCount: filesPayload.length,
      files: filesPayload.map(f => ({ filename: f.filename, type: f.type })),
    });

    const response = await api.post(`${WHATSAPP_BASE}/messages/send-with-media`, {
      number,
      text: hasText ? text : null,
      files: filesPayload,
    });

    return response.data;
  }

  /**
   * Mark messages as read
   * @param {string} remoteJid - Chat JID
   * @param {string} messageId - Message ID to mark as read
   */
  async markAsRead(remoteJid, messageId) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/mark-read`, {
      remote_jid: remoteJid,
      message_id: messageId,
    });
    return response.data;
  }

  /**
   * Send typing indicator
   * @param {string} number - Phone number
   * @param {boolean} typing - True to start, false to stop
   */
  async sendTyping(number, typing = true) {
    const response = await api.post(
      `${WHATSAPP_BASE}/messages/typing?number=${number}&typing=${typing}`
    );
    return response.data;
  }

  // ============================================
  // CONTACTS
  // ============================================

  /**
   * List all contacts
   */
  async getContacts() {
    const response = await api.get(`${WHATSAPP_BASE}/contacts`);
    return response.data;
  }

  /**
   * Get profile picture URL
   * @param {string} number - Phone number
   */
  async getProfilePicture(number) {
    const response = await api.get(`${WHATSAPP_BASE}/contacts/${number}/picture`);
    return response.data;
  }

  /**
   * Check if numbers are on WhatsApp
   * @param {Array} numbers - List of phone numbers
   */
  async checkWhatsAppNumbers(numbers) {
    const response = await api.post(`${WHATSAPP_BASE}/contacts/check`, {
      numbers,
    });
    return response.data;
  }

  // ============================================
  // GROUPS
  // ============================================

  /**
   * List all groups
   */
  async getGroups() {
    const response = await api.get(`${WHATSAPP_BASE}/groups`);
    return response.data;
  }

  /**
   * Get group info
   * @param {string} groupJid - Group JID
   */
  async getGroupInfo(groupJid) {
    const encodedJid = encodeURIComponent(groupJid);
    const response = await api.get(`${WHATSAPP_BASE}/groups/${encodedJid}`);
    return response.data;
  }

  // ============================================
  // UTILITIES
  // ============================================

  /**
   * Extract phone number from JID
   * @param {string} jid - WhatsApp JID
   */
  extractNumber(jid) {
    if (!jid) return '';
    return jid.split('@')[0];
  }

  /**
   * Check if JID is a group
   * @param {string} jid - WhatsApp JID
   */
  isGroup(jid) {
    return jid?.includes('@g.us') || false;
  }

  /**
   * Format phone number (remove special chars)
   * @param {string} number - Phone number
   */
  formatNumber(number) {
    if (!number) return '';
    return number.replace(/[^0-9]/g, '');
  }

  /**
   * Extract filename from URL
   * @private
   * @param {string} url - File URL
   */
  _extractFilename(url) {
    if (!url) return 'arquivo';
    try {
      const cleanUrl = url.split('?')[0];
      const filename = cleanUrl.split('/').pop();
      return decodeURIComponent(filename) || 'arquivo';
    } catch {
      return 'arquivo';
    }
  }

  // ============================================
  // MEDIA DOWNLOAD
  // ============================================

  /**
   * Download media from a message
   * 
   * Uses Evolution API's getBase64FromMediaMessage to retrieve
   * the full media content as base64.
   * 
   * @param {string} messageKeyId - The key.id from the message
   * @param {boolean} convertToMp4 - Convert video to MP4
   * @returns {Promise<Object>} Media data with base64, mimetype, fileName
   */
  async downloadMedia(messageKeyId, convertToMp4 = false) {
    if (!messageKeyId) {
      throw new Error('messageKeyId is required');
    }

    console.log('[WhatsApp] Downloading media:', messageKeyId);

    try {
      const response = await api.post(`${WHATSAPP_BASE}/media/download`, {
        message_key_id: messageKeyId,
        convert_to_mp4: convertToMp4,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.detail || 'Download failed');
      }

      const data = response.data.data;
      console.log('[WhatsApp] Media downloaded:', {
        type: data.mediaType,
        fileName: data.fileName,
        mimetype: data.mimetype,
      });

      return data;
    } catch (error) {
      console.error('[WhatsApp] Media download error:', error);
      throw error;
    }
  }

  /**
   * Download media from a message object
   * Convenience method that extracts the key.id automatically
   * 
   * @param {Object} messageKey - The message.key object
   * @param {string} mediaType - Optional: force media type (image, audio, video, document)
   * @returns {Promise<Object>} Media data with base64, mimetype, fileName
   */
  async downloadMediaFromMessage(messageKey, mediaType = null) {
    if (!messageKey?.id) {
      throw new Error('Invalid message key');
    }

    // For videos, convert to MP4 for better compatibility
    const convertToMp4 = mediaType === 'video';

    return this.downloadMedia(messageKey.id, convertToMp4);
  }
}

// Export singleton instance
const whatsappService = new WhatsAppService();
export default whatsappService;